import React, { useEffect, useRef, useCallback } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useSearchParams, useNavigate } from "react-router-dom";
import { BsFillGridFill } from "react-icons/bs";
import { FaThList } from "react-icons/fa";
import ShopProducts from "../components/products/ShopProducts";
import { useDispatch, useSelector } from "react-redux";
import { price_range_product, query_products } from "../store/reducers/homeReducer";
import { Helmet } from "react-helmet";
import { PropagateLoader } from "react-spinners";

import { useFilterState } from "../hooks/useFilterState";
import {
  CatalogActiveFilters,
  CatalogFilterPanel,
  CatalogFilterToggleButton,
  FILTER_ARRAY_KEYS,
  asFilterArray,
  hasActiveCatalogFilters,
} from "../components/global/ProductFilters";

// Importar ThemeContext
import { useTheme } from "../context/ThemeContext";

const SearchProducts = () => {
  const [searchParams] = useSearchParams();
 
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Usar el ThemeContext
  const { getBackground, getBorder, getColor, getTextColor, getButtonStyles,  getMuiTextStyle } = useTheme();

  // Ref para rastrear la URL anterior
  const previousSearchRef = useRef("");
  // Refs para scroll infinito (sin filters aún)
  const sentinelRef = useRef(null);
  const loadingRef = useRef(false);
  const filtersRef = useRef(null);

  // Obtener parámetros de la URL
  const urlCategory = searchParams.get("category");
  const urlTypearticle = searchParams.get("typearticle");
  const urlBrand = searchParams.get("brand");
  const urlSearchValue = searchParams.get("value");
  const urlPage = searchParams.get("page");
  const urlRating = searchParams.get("rating");

  const {
    products = [],
    categorys = [],
    typeproducts = [],
    typearticles = [],
    brands = [],
    colors = [],
    sizes = [],
    priceRange,
    totalProduct = 0,
  } = useSelector(state => state.home || {});
  const filterOptions = { categorys, typeproducts, typearticles, brands, colors, sizes };

  // Usar el hook personalizado
  const { filters, expandedSections, updateFilter, handleCheckboxChange, handleResetRating, handlePriceChange, toggleSection, resetFilters } = useFilterState(
    {
      category: urlCategory ? [urlCategory] : [],
      typearticle: urlTypearticle ? [urlTypearticle] : [],
      brand: urlBrand ? [urlBrand] : [],
      rating: urlRating || "",
      searchValue: urlSearchValue || "",
      pageNumber: urlPage ? parseInt(urlPage) : 1,
    },
    { priceRange }
  );

  // Ahora sí podemos usar filters y products
  useEffect(() => { filtersRef.current = filters; }, [filters]);
  const hasMore = products.length < totalProduct;

  // Effect para sincronizar filtros desde URL - se ejecuta siempre que cambie searchParams
  useEffect(() => {
    const currentSearch = searchParams.toString();
    if (currentSearch === previousSearchRef.current) return;
    previousSearchRef.current = currentSearch;

    // Aplicar todos los valores de la URL directamente, sin comparar con filters
    updateFilter("category",    urlCategory ? [urlCategory] : []);
    updateFilter("typearticle", urlTypearticle ? [urlTypearticle] : []);
    updateFilter("brand",       urlBrand ? [urlBrand] : []);
    updateFilter("rating",      urlRating      || "");
    updateFilter("searchValue", urlSearchValue || "");
    updateFilter("pageNumber",  1);
    loadingRef.current = false;
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  // Effect para cargar datos iniciales
  useEffect(() => {
    dispatch(price_range_product());
  }, [dispatch]);

  // Effect para dispatch de productos
 const {
    category,
    typearticle,
    brand,
    rating,
    pageNumber,
    priceValues,
    typeproduct,
    color,
    size,
    sortPrice,
    searchValue,
  } = filters;

  // Extraemos low y highValue del array, si es necesario, aunque a veces no hace falta si usas priceValues en la llamada
  const lowValue = priceValues[0];
  const highValue = priceValues[1];


  useEffect(() => {
    // Usamos un pequeño retraso (debounce) para no disparar la API con cada pulsación de tecla o cambio de slider
    const timer = setTimeout(() => {
      console.log("📦 Dispatcheando productos con:", {
        category,
        typearticle,
        brand,
        rating,
        pageNumber,
      });

      dispatch(
        query_products({
          low: lowValue,
          high: highValue,
          category,
          typeproduct,
          typearticle,
          brand,
          color,
          size,
          rating,
          sortPrice,
          pageNumber,
          searchValue: urlSearchValue || searchValue,
        })
      );
      loadingRef.current = false;
    }, 50); // Un debounce corto de 50ms

    // Función de limpieza: cancela el timer si los filtros cambian antes de que se complete el tiempo
    return () => clearTimeout(timer);

  }, [
    // AÑADIDO: 'dispatch' es una dependencia estable por defecto
    dispatch, 
    // CAMBIADO: Usamos las variables simples extraídas, no accesos complejos a objetos
    category,
    typearticle,
    brand,
    rating,
    pageNumber,
    lowValue,         
    highValue,        
    typeproduct,
    color,
    size,
    sortPrice,
    urlSearchValue,
    searchValue, // Asegúrate de incluir searchValue si lo usas en la lógica interna del efecto
  ]);
  

  // Scroll infinito
  const loadMore = useCallback(() => {
    if (loadingRef.current) return;
    if (products.length >= totalProduct) return;
    loadingRef.current = true;
    updateFilter("pageNumber", filtersRef.current.pageNumber + 1);
  }, [products.length, totalProduct, updateFilter]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => { if (entries[0].isIntersecting) loadMore(); },
      { threshold: 0.1 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  // Handlers
  const removeFilter = useCallback(
    (filterKey, value = "") => {
      if (FILTER_ARRAY_KEYS.includes(filterKey)) {
        const currentValues = asFilterArray(filtersRef.current[filterKey]);
        updateFilter(filterKey, value ? currentValues.filter(item => item !== value) : []);
      } else {
        updateFilter(filterKey, "");
      }
      if (filterKey === "rating") handleResetRating();
      if (filterKey === "priceValues" && priceRange) handlePriceChange([priceRange.low, priceRange.high]);
    },
    [updateFilter, handleResetRating, handlePriceChange, priceRange]
  );

  const clearAllFilters = useCallback(() => {
    resetFilters();
    if (priceRange?.low && priceRange?.high) handlePriceChange([priceRange.low, priceRange.high]);
  }, [resetFilters, handlePriceChange, priceRange]);

  const hasActiveFilters = hasActiveCatalogFilters(filters, priceRange);

  // Estilos dinámicos con ThemeContext
  const primaryColor = getColor("primary", "#ae1d22");
  const secondaryColor = getColor("secondary", "#dc3545");
  const infoColor = getColor("info", "#17a2b8");
  // Estilo para el header de resultados
  const resultsHeaderStyle = {
    ...getMuiTextStyle("textPrimary"),
    fontSize: "1.25rem",
    fontWeight: "600",
  };

  // Estilo para el select de ordenamiento
  const selectStyle = {
    ...getBorder("default"),
    backgroundColor: getBackground("surface", "#ffffff").backgroundColor,
    color: getTextColor("secondary").color,
    padding: "0.625rem 2.5rem 0.625rem 2.75rem",
    borderRadius: "0.375rem",
    fontSize: "0.875rem",
    fontWeight: "500",
    outline: "none",
    transition: "all 0.2s ease",
    "&:hover": {
      borderColor: getColor("primary", "#ae1d22"),
    },
    "&:focus": {
      borderColor: getColor("primary", "#ae1d22"),
      boxShadow: `0 0 0 3px ${getColor("primary", "#ae1d22")}20`,
    },
  };

  return (
    <div>
      <Header />
      <Helmet>
        <title>Resultados de búsqueda | Tekos.pe</title>
        <meta name="description" content="Encuentra los mejores productos en Tekos.pe. Busca por categoría, marca, tipo de artículo y más." />
        <meta name="keywords" content="búsqueda, productos, filtros, tienda online" />
      </Helmet>

      <section className="py-8">
        <div className="w-[85%] md:w-[80%] sm:w-[90%] lg:w-[90%] h-full mx-auto">
          {/* Mostrar término de búsqueda */}
          {urlSearchValue && (
            <div
              className="mb-6 p-5 rounded-xl shadow-sm"
              style={{
                background: `linear-gradient(135deg, ${getColor("infoLight", "#d1ecf1")} 0%, ${getColor("primaryLight", "#f8d7da")} 100%)`,
                border: `1px solid ${getColor("info", "#17a2b8")}20`,
              }}>
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                  <h2 className="text-xl font-bold" style={{ color: getColor("infoDark", "#0c5460") }}>
                    Resultados de búsqueda para:
                    <span className="font-extrabold ml-2 text-2xl" style={{ color: getColor("primaryDark", "#721c24") }}>
                      "{urlSearchValue}"
                    </span>
                  </h2>
                  <p className="text-sm mt-2 flex items-center gap-2" style={{ color: getColor("primary", "#17a2b8") }}>
                    Encontrados <span className="font-bold mx-1">{totalProduct}</span> producto{totalProduct !== 1 ? "s" : ""}
                  </p>
                </div>
                <div className="flex gap-3">
                  <button
                    onClick={() => navigate("/shops")}
                    style={{
                      ...getButtonStyles("primary", "white", "transparent", infoColor),
                      padding: "0.625rem 1.25rem",
                      borderRadius: "0.5rem",
                      fontSize: "0.875rem",
                      fontWeight: "500",
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                    }}>
                    Ver todos los productos
                  </button>
                  {hasActiveFilters && (
                    <button
                      onClick={clearAllFilters}
                      style={{
                        ...getButtonStyles("secondary", "dark", "transparent", "#6c757d"),
                        padding: "0.625rem 1.25rem",
                        borderRadius: "0.5rem",
                        fontSize: "0.875rem",
                        fontWeight: "500",
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                      }}>
                      Limpiar filtros
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          <CatalogActiveFilters
            filters={filters}
            priceRange={priceRange}
            onRemove={removeFilter}
            onClear={clearAllFilters}
          />
          {/* Boton para mostrar/ocultar filtros (solo movil) */}
          <div className="md:block hidden mb-6">
            <CatalogFilterToggleButton
              isOpen={filters.filterVisible}
              onClick={() => updateFilter("filterVisible", !filters.filterVisible)}
              hasActiveFilters={hasActiveFilters}
            />
          </div>
          <div className="w-full flex flex-wrap">
            {/* Columna de filtros */}
            <div className={`w-3/12 md-lg:w-4/12 md:w-full pr-8 ${filters.filterVisible ? "md:hidden" : ""}`}>
              <CatalogFilterPanel
                filterOptions={filterOptions}
                filters={filters}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
                onCheckboxChange={handleCheckboxChange}
                priceRange={priceRange}
                onPriceChange={handlePriceChange}
                idPrefix="search-products"
              />
            </div>
            {/* Columna de productos */}
            <div className="w-9/12 md-lg:w-8/12 md:w-full">
              <div className="pl-8 md:pl-0">
                <div
                  className="py-4 mb-10 px-6 rounded-lg flex justify-between items-center border shadow-sm"
                  style={{
                    ...getBorder("default"),
                    backgroundColor: getBackground("card", "#ffffff").backgroundColor,
                    boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                  }}>
                  <div>
                    <h2 style={resultsHeaderStyle}>
                      {totalProduct} Producto{totalProduct !== 1 ? "s" : ""} Disponible{totalProduct !== 1 ? "s" : ""}
                    </h2>
                    {urlSearchValue && (
                      <p className="text-sm mt-1" style={{ color: getTextColor("textSecondary").color }}>
                        Resultados para: "{urlSearchValue}"
                      </p>
                    )}
                    {hasActiveFilters && (
                      <p className="text-sm mt-1" style={{ color: getTextColor("textSecondary").color }}>
                        Filtrados según tu selección
                      </p>
                    )}
                  </div>
                  <div className="flex justify-center items-center gap-4">
                    <div className="relative">
                      <select
                        onChange={e => updateFilter("sortPrice", e.target.value)}
                        className="p-2.5 pl-10 border appearance-none bg-white pr-10"
                        style={selectStyle}
                        value={filters.sortPrice}
                        aria-label="Ordenar productos">
                        <option value="">Ordenar por</option>
                        <option value="low-to-high">Precio: Bajo a Alto</option>
                        <option value="high-to-low">Precio: Alto a Bajo</option>
                      </select>
                      <div className="absolute left-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style={{ color: getTextColor("textSecondary").color }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path>
                        </svg>
                      </div>
                    </div>
                    <div className="flex justify-center items-center gap-2 md-lg:hidden p-1 rounded-lg" style={{ backgroundColor: getBackground("surface", "#e9ecef").backgroundColor }}>
                      <button
                        onClick={() => updateFilter("styles", "grid")}
                        className={`p-2.5 rounded-md cursor-pointer transition-all ${filters.styles === "grid" ? "bg-white shadow-sm" : "hover:text-gray-700"}`}
                        style={{
                          color: filters.styles === "grid" ? primaryColor : getTextColor("textSecondary").color,
                        }}
                        aria-label="Vista de cuadrícula">
                        <BsFillGridFill size={18} />
                      </button>

                      <button
                        onClick={() => updateFilter("styles", "list")}
                        className={`p-2.5 rounded-md cursor-pointer transition-all ${filters.styles === "list" ? "bg-white shadow-sm" : "hover:text-gray-700"}`}
                        style={{
                          color: filters.styles === "list" ? primaryColor : getTextColor("textSecondary").color,
                        }}
                        aria-label="Vista de lista">
                        <FaThList size={16} />
                      </button>
                    </div>
                  </div>
                </div>

                <div className="pb-8">
                  {products?.length > 0 ? (
                    <ShopProducts products={products} styles={filters.styles} themePrimaryColor={primaryColor} themeSecondaryColor={secondaryColor} />
                  ) : (
                    <div className="text-center py-16">
                      <div className="max-w-md mx-auto">
                        <svg
                          className="w-24 h-24 mx-auto mb-6"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                          style={{ color: getColor("textDisabled", "#adb5bd") }}>
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                        </svg>
                        <h3 className="text-2xl font-semibold mb-3" style={{ color: getTextColor("textPrimary").color }}>
                          {urlSearchValue ? "No se encontraron resultados" : "No hay productos disponibles"}
                        </h3>
                        <p className="mb-6" style={{ color: getTextColor("textSecondary").color }}>
                          {urlSearchValue
                            ? `No encontramos productos para "${urlSearchValue}". Prueba con otros términos de búsqueda o ajusta los filtros.`
                            : "No hay productos que coincidan con los filtros seleccionados. Intenta con otros criterios de búsqueda."}
                        </p>
                        <div className="flex flex-col sm:flex-row gap-3 justify-center">
                          {hasActiveFilters && (
                            <button
                              onClick={clearAllFilters}
                              style={{
                                ...getButtonStyles("primary", "white", "transparent", primaryColor),
                                padding: "0.75rem 1.5rem",
                                borderRadius: "0.5rem",
                                fontSize: "1rem",
                                fontWeight: "500",
                              }}>
                              Limpiar todos los filtros
                            </button>
                          )}
                          <button
                            onClick={() => navigate("/shops")}
                            style={{
                              ...getButtonStyles("secondary", "dark", "transparent", "#6c757d"),
                              padding: "0.75rem 1.5rem",
                              borderRadius: "0.5rem",
                              fontSize: "1rem",
                              fontWeight: "500",
                            }}>
                            Ver todos los productos
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Sentinel scroll infinito */}
                <div ref={sentinelRef} className="w-full h-4" />

                {hasMore && (
                  <div className="flex justify-center py-6">
                    <PropagateLoader color={primaryColor} size={12} />
                  </div>
                )}

                {!hasMore && products.length > 0 && (
                  <p className="text-center text-gray-400 text-sm py-6">
                    — {totalProduct} producto{totalProduct !== 1 ? 's' : ''} mostrado{totalProduct !== 1 ? 's' : ''} —
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
      <Footer />
    </div>
  );
};

export default SearchProducts;
